import { useEffect, useRef } from 'react';

import pluralize from 'pluralize';

import { CAST_AI } from '@cast/constants';
import {
  Button,
  DialogV2,
  Inset,
  makeDialog,
  notify,
  useCloseDialog,
  ExternalLink,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { useMarkSalesContactedMutation } from '@cast/shared-trial-data-access';
import { useTrial, PremiumFeaturesList } from '@cast/shared-trial-ui';

import { openScheduleDialog } from 'core/integrations';

import { getDocsLink } from '@cast/utils';

type TrialInProgressDialogContentProps = {
  onClose?: () => void;
  onSettle?: () => void;
};

const TrialInProgressDialogContent = ({
  onClose,
  onSettle,
}: TrialInProgressDialogContentProps) => {
  const closeDialog = useCloseDialog();
  const { expiresInDays, isAboutToExpire } = useTrial();
  const { mutateAsync: markSalesContacted } = useMarkSalesContactedMutation();

  const settledRef = useRef(false);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const onSettleRef = useRef(onSettle);
  onSettleRef.current = onSettle;

  useEffect(() => {
    return () => {
      if (settledRef.current) {
        onSettleRef.current?.();
      } else {
        onCloseRef.current?.();
      }
    };
  }, []);

  const handleScheduleCall = () => {
    openScheduleDialog({
      onMeetingBooked: () => {
        markSalesContacted().then(() => {
          settledRef.current = true;
          closeDialog();
        });
      },
      onError: () =>
        notify.error(
          'Failed to load the booking form. Please try again later.'
        ),
    });
  };

  const title = (
    <Typography
      variant="h5"
      alignItems="center"
      component="span"
      color={tokens.theme.fg.structural.primary}
    >
      {isAboutToExpire && <>⏳ </>}
      {expiresInDays === 0 ? (
        <>
          Your trial expires{' '}
          <Typography
            variant="inherit"
            color={
              isAboutToExpire
                ? tokens.theme.fg.status.error
                : tokens.theme.fg.structural.primary
            }
            component="span"
          >
            today
          </Typography>
        </>
      ) : (
        <>
          <Typography
            variant="inherit"
            color={
              isAboutToExpire
                ? tokens.theme.fg.status.error
                : tokens.theme.fg.structural.primary
            }
            component="span"
          >
            {expiresInDays} {pluralize('day', expiresInDays)}
          </Typography>{' '}
          left of your trial
        </>
      )}
    </Typography>
  );

  return (
    <DialogV2
      testId="trial-in-progress-dialog"
      title={title}
      footerDivider
      endActions={
        <Button
          variant="primary"
          testId="schedule-sales-call-button"
          onClick={handleScheduleCall}
        >
          Talk to us to upgrade
        </Button>
      }
    >
      <Inset pb={24}>
        <Stack gap={16}>
          <Stack gap={8}>
            <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
              Don&apos;t lose access to these premium features:
            </Typography>
            <PremiumFeaturesList />
          </Stack>

          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            Ready to see how {CAST_AI} can continue to drive success for your
            organization? Schedule a call with our team to explore your options
            and maximize your savings potential.
          </Typography>
        </Stack>

        <Stack mt={32} gap={4}>
          <Typography variant="h6" color={tokens.theme.fg.structural.primary}>
            What happens to my clusters after the trial ends?
          </Typography>

          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
          >
            A downgrading process will take place to transition your account
            back to the free version, ensuring your infrastructure is preserved.
            For more details,{' '}
            <ExternalLink link={getDocsLink('docs_trial')} noIcon>
              see our documentation
            </ExternalLink>
            .
          </Typography>
        </Stack>
      </Inset>
    </DialogV2>
  );
};

export const useTrialInProgressDialog = makeDialog(
  TrialInProgressDialogContent
);
